import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Plus, Edit3, Trash2, Check, X, Calendar as CalIcon, List, Clock, Loader2,
  Phone, MessageCircle, MapPin, Image as ImageIcon, Save, NotebookPen, ArrowRight, Clock3, Settings as SettingsIcon, AlertCircle,
} from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import { api, fileUrl, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { buildWhatsappMessage, whatsappLink } from "../lib/whatsapp";

const VIEWS = [
  { id: "today", label: "Heute", icon: Clock },
  { id: "week", label: "Woche", icon: CalIcon },
  { id: "calendar", label: "Kalender", icon: CalIcon },
  { id: "list", label: "Liste", icon: List },
];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Monday=0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function fmtISO(d) { return d.toISOString().slice(0, 10); }
function fmtHuman(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "short" }); }
  catch { return s; }
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [view, setView] = useState("today");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waNumber, setWaNumber] = useState("4917656198464");
  const [editing, setEditing] = useState(null); // quote being edited
  const [showNew, setShowNew] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/admin/login");
      return;
    }
    loadAll();
    api.get("/config").then(({ data }) => data?.whatsapp_number && setWaNumber(data.whatsapp_number)).catch(() => {});
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
  }, [navigate]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Konnte Anfragen nicht laden");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  const filtered = useMemo(() => {
    const today = fmtISO(new Date());
    const sow = startOfWeek(new Date());
    const eow = new Date(sow); eow.setDate(eow.getDate() + 7);
    return quotes.filter((q) => {
      if (view === "today") return q.moving_date === today;
      if (view === "week") {
        const d = new Date(q.moving_date);
        return d >= sow && d < eow;
      }
      return true;
    });
  }, [quotes, view]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const q of filtered) {
      const k = q.moving_date || "—";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(q);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const updateQuote = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, patch);
      setQuotes((prev) => prev.map((q) => (q.id === id ? data : q)));
      toast.success("Gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Diese Anfrage wirklich löschen?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((prev) => prev.filter((q) => q.id !== id));
      toast.success("Gelöscht");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const stats = useMemo(() => {
    const pending = quotes.filter((q) => q.status === "pending").length;
    const confirmed = quotes.filter((q) => q.status === "confirmed").length;
    const today = fmtISO(new Date());
    const todayCount = quotes.filter((q) => q.moving_date === today).length;
    return { pending, confirmed, todayCount, total: quotes.length };
  }, [quotes]);

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Admin</div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">Anfragen-Übersicht</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => navigate("/admin/cms")} className="inline-flex items-center gap-2 rounded-xl bg-brand-amber text-brand-navy px-4 py-2.5 text-sm font-bold hover:bg-amber-300 transition-colors" data-testid="admin-cms-link">
              <SettingsIcon size={16} /> Website bearbeiten
            </button>
            <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-xl bg-brand-navy text-white px-4 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors" data-testid="admin-new">
              <Plus size={16} /> Neuer Termin
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white transition-colors" data-testid="admin-logout">
              <LogOut size={16} /> Abmelden
            </button>
          </div>
        </div>

        {me && me.password_changed === false && (
          <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="default-pwd-warning">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <strong>Bitte ändern Sie Ihr Admin-Passwort vor Veröffentlichung.</strong>{" "}
              <button onClick={() => navigate("/admin/cms")} className="underline font-semibold">Jetzt ändern →</button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Heute" value={stats.todayCount} />
          <StatCard label="Offen" value={stats.pending} tone="pending" />
          <StatCard label="Bestätigt" value={stats.confirmed} tone="confirmed" />
          <StatCard label="Gesamt" value={stats.total} />
        </div>

        {/* View tabs */}
        <div className="mt-6 inline-flex bg-white border border-slate-200 rounded-2xl p-1 shadow-soft" data-testid="admin-views">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                view === v.id ? "bg-brand-navy text-white" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`view-${v.id}`}
            >
              <v.icon size={14} /> {v.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="mt-10 grid place-items-center text-slate-500"><Loader2 className="animate-spin" /></div>
        ) : view === "calendar" ? (
          <CalendarView quotes={quotes} onSelect={(q) => setEditing(q)} />
        ) : (
          <div className="mt-6 space-y-8">
            {grouped.length === 0 && (
              <div className="rounded-3xl bg-white border border-slate-200 p-10 text-center text-slate-500" data-testid="empty">
                Keine Anfragen für diesen Zeitraum.
              </div>
            )}
            {grouped.map(([date, items]) => (
              <div key={date}>
                <div className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">{fmtHuman(date)} · {date}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {items.map((q) => (
                    <QuoteCard key={q.id} q={q} waNumber={waNumber} onEdit={() => setEditing(q)} onDelete={() => deleteQuote(q.id)} onQuickConfirm={(price) => updateQuote(q.id, { status: "confirmed", final_price: price })} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <EditDialog q={editing} onClose={() => setEditing(null)} onSave={async (patch) => { await updateQuote(editing.id, patch); setEditing(null); }} />
      )}
      {showNew && (
        <NewQuoteDialog onClose={() => setShowNew(false)} onCreated={(q) => { setQuotes((prev) => [q, ...prev]); setShowNew(false); setView("list"); }} />
      )}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const bg = tone === "pending" ? "bg-amber-50 border-amber-200" : tone === "confirmed" ? "bg-green-50 border-green-200" : "bg-white border-slate-200";
  const txt = tone === "pending" ? "text-amber-800" : tone === "confirmed" ? "text-green-800" : "text-slate-900";
  return (
    <div className={`rounded-2xl border ${bg} p-4 shadow-soft`}>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 text-3xl font-heading font-extrabold ${txt}`}>{value}</div>
    </div>
  );
}

function ArrowRightIcon() {
  return <ArrowRight size={14} className="text-slate-400" />;
}

function extractCity(addr) {
  if (!addr) return "";
  // Try to grab the last token containing letters; works well for "Strasse 12, 52062 Aachen"
  const parts = addr.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return addr;
  const last = parts[parts.length - 1];
  // Remove postcodes from city token
  return last.replace(/\b\d{4,5}\b/g, "").trim() || last;
}

function QuoteCard({ q, waNumber, onEdit, onDelete, onQuickConfirm }) {
  const isConfirmed = q.status === "confirmed";
  const isCancelled = q.status === "cancelled";
  const bg = isCancelled ? "bg-slate-100 border-slate-300" : isConfirmed ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200";
  const headTxt = isCancelled ? "text-slate-600" : isConfirmed ? "text-green-900" : "text-amber-900";
  const [priceInput, setPriceInput] = useState("");

  const fromCity = extractCity(q.pickup_address);
  const toCity = extractCity(q.delivery_address);
  const timeLabel = q.moving_time ? `${q.moving_time} Uhr` : (q.time_window || "");

  const photoUrls = (q.photos || []).map((p) =>
    p?.url?.startsWith("http") ? p.url : `${BACKEND_URL}${p.url || ""}`
  ).filter(Boolean);
  const waLink = whatsappLink(waNumber, buildWhatsappMessage({ ...q, photo_urls: photoUrls }));

  return (
    <div className={`rounded-2xl border ${bg} p-4 sm:p-5 shadow-soft`} data-testid={`quote-card-${q.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest ${headTxt}`}>
            {q.type === "small" ? "Kleine Transporte" : "Kompletter Umzug"} · {q.status}
          </div>
          <div className="mt-1 font-heading font-bold text-slate-900 text-lg truncate">{q.first_name} {q.last_name}</div>
          {(fromCity || toCity) && (
            <div className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-800" data-testid={`route-${q.id}`}>
              <span className="truncate max-w-[120px]">{fromCity || "—"}</span>
              <ArrowRightIcon />
              <span className="truncate max-w-[120px]">{toCity || "—"}</span>
            </div>
          )}
          {timeLabel && (
            <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-slate-700" data-testid={`time-${q.id}`}>
              <Clock3 size={12} /> {timeLabel}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          {isConfirmed && q.final_price != null ? (
            <div className="text-2xl font-heading font-extrabold text-green-800">{q.final_price}€</div>
          ) : (
            <div className="text-base font-heading font-bold text-amber-900 whitespace-nowrap">{q.estimate_min}€ – {q.estimate_max}€</div>
          )}
          <div className="text-[11px] text-slate-500 mt-0.5">{q.moving_date}</div>
        </div>
      </div>

      <div className="mt-3 space-y-1 text-sm text-slate-700">
        <div className="flex items-start gap-2"><MapPin size={14} className="text-slate-500 mt-0.5" /> <span><b>Von:</b> {q.pickup_address} (Etage {q.pickup_floor}, Aufzug {q.pickup_elevator ? "Ja" : "Nein"})</span></div>
        <div className="flex items-start gap-2"><MapPin size={14} className="text-slate-500 mt-0.5" /> <span><b>Nach:</b> {q.delivery_address} (Etage {q.delivery_floor}, Aufzug {q.delivery_elevator ? "Ja" : "Nein"})</span></div>
        <div className="flex items-center gap-3 pt-1">
          <a href={`tel:${q.phone}`} className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 text-sm"><Phone size={13} /> {q.phone}</a>
          <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-green-700 hover:text-green-800 text-sm font-semibold"><MessageCircle size={13} /> WhatsApp</a>
        </div>
      </div>

      {(q.detected_items?.length > 0 || q.job_type || q.effort) && (
        <div className="mt-3" data-testid={`detected-${q.id}`}>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">KI · Scanner</div>
          <div className="flex flex-wrap gap-1">
            {q.job_type && (
              <span className="inline-flex items-center rounded-full bg-brand-navy/10 border border-brand-navy/20 px-2 py-0.5 text-[11px] font-semibold text-brand-navy">
                {q.job_type}
              </span>
            )}
            {q.effort && (
              <span className="inline-flex items-center rounded-full bg-amber-100 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                Aufwand · {q.effort}
              </span>
            )}
            {(q.detected_items || []).map((it, i) => (
              <span key={i} className="inline-flex items-center rounded-full bg-white/80 border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700">{it}</span>
            ))}
          </div>
        </div>
      )}

      {q.photos?.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Fotos · {q.photos.length}</div>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5" data-testid={`photos-${q.id}`}>
            {q.photos.slice(0, 8).map((p) => (
              <a
                key={p.id}
                href={fileUrl(p.url)}
                target="_blank"
                rel="noreferrer"
                title="Bild im neuen Tab öffnen"
                className="group relative aspect-square rounded-lg overflow-hidden border border-white/70 bg-slate-200 hover:ring-2 hover:ring-brand-amber transition-all"
              >
                <img src={fileUrl(p.url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 grid place-items-center transition-colors">
                  <ImageIcon size={14} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
            ))}
            {q.photos.length > 8 && (
              <div className="aspect-square rounded-lg bg-slate-100 border border-white/70 grid place-items-center text-xs font-bold text-slate-600">
                +{q.photos.length - 8}
              </div>
            )}
          </div>
        </div>
      )}

      {q.internal_notes && (
        <div className="mt-3 rounded-xl bg-white/70 border border-slate-200 p-3 text-xs text-slate-700">
          <div className="flex items-center gap-1 font-bold text-slate-500 uppercase tracking-widest text-[10px] mb-1"><NotebookPen size={12} /> Notiz</div>
          {q.internal_notes}
        </div>
      )}

      {!isConfirmed && !isCancelled && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input
            type="number"
            placeholder="Finaler Preis €"
            value={priceInput}
            onChange={(e) => setPriceInput(e.target.value)}
            className="w-32 rounded-xl border border-white px-3 py-2 text-sm bg-white"
            data-testid={`final-price-${q.id}`}
          />
          <button
            onClick={() => priceInput && onQuickConfirm(+priceInput)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-green-600 hover:bg-green-700 text-white px-3 py-2 text-sm font-semibold transition-colors"
            data-testid={`confirm-${q.id}`}
          >
            <Check size={14} /> Bestätigen
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        <button onClick={onEdit} className="inline-flex items-center gap-1 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700" data-testid={`edit-${q.id}`}>
          <Edit3 size={13} /> Bearbeiten
        </button>
        <button onClick={onDelete} className="inline-flex items-center gap-1 rounded-lg bg-white border border-red-200 hover:bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600" data-testid={`delete-${q.id}`}>
          <Trash2 size={13} /> Löschen
        </button>
      </div>
    </div>
  );
}

function CalendarView({ quotes, onSelect }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); d.setDate(1); return d;
  });
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDayIdx = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const byDate = useMemo(() => {
    const m = {};
    quotes.forEach((q) => { (m[q.moving_date] ||= []).push(q); });
    return m;
  }, [quotes]);

  const monthLabel = first.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  const dayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

  return (
    <div className="mt-6 rounded-3xl bg-white border border-slate-200 shadow-soft p-4 sm:p-6" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50">←</button>
        <div className="font-heading font-bold text-slate-900 text-lg capitalize">{monthLabel}</div>
        <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50">→</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs text-slate-500 mb-1">
        {dayNames.map((d) => <div key={d} className="text-center font-semibold py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: startDayIdx }).map((_, i) => <div key={`p${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1;
          const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
          const items = byDate[iso] || [];
          const hasConfirmed = items.some((q) => q.status === "confirmed");
          const hasPending = items.some((q) => q.status === "pending");
          return (
            <div key={iso} className="min-h-[80px] rounded-xl border border-slate-100 p-1.5 hover:border-slate-200 transition-colors">
              <div className="text-xs font-semibold text-slate-500">{dayNum}</div>
              <div className="space-y-1 mt-1">
                {items.slice(0, 3).map((q) => (
                  <button key={q.id} onClick={() => onSelect(q)} className={`w-full text-left rounded-md px-1.5 py-0.5 text-[10px] font-semibold truncate ${
                    q.status === "confirmed" ? "bg-green-100 text-green-800" : q.status === "cancelled" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-900"
                  }`} data-testid={`cal-${q.id}`}>
                    {q.first_name} {q.last_name}
                  </button>
                ))}
                {items.length > 3 && <div className="text-[10px] text-slate-400">+{items.length - 3}</div>}
              </div>
              {(hasConfirmed || hasPending) && <div className="sr-only">{hasConfirmed ? "confirmed" : "pending"}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EditDialog({ q, onClose, onSave }) {
  const [form, setForm] = useState({
    status: q.status,
    final_price: q.final_price ?? "",
    first_name: q.first_name,
    last_name: q.last_name,
    phone: q.phone,
    whatsapp: q.whatsapp || "",
    pickup_address: q.pickup_address,
    delivery_address: q.delivery_address,
    moving_date: q.moving_date,
    moving_time: q.moving_time || "",
    time_window: q.time_window || "",
    internal_notes: q.internal_notes || "",
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-floating w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 sm:p-7" onClick={(e) => e.stopPropagation()} data-testid="edit-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-heading font-bold text-slate-900">Anfrage bearbeiten</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="space-y-3 text-sm">
          <Row label="Status">
            <select value={form.status} onChange={(e) => update("status", e.target.value)} className="input-2" data-testid="edit-status">
              <option value="pending">pending</option>
              <option value="confirmed">confirmed</option>
              <option value="cancelled">cancelled</option>
            </select>
          </Row>
          <Row label="Finaler Preis (€)">
            <input type="number" value={form.final_price} onChange={(e) => update("final_price", e.target.value)} className="input-2" data-testid="edit-price" />
          </Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Vorname"><input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className="input-2" /></Row>
            <Row label="Nachname"><input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className="input-2" /></Row>
          </div>
          <Row label="Telefon"><input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="input-2" /></Row>
          <Row label="WhatsApp"><input value={form.whatsapp} onChange={(e) => update("whatsapp", e.target.value)} className="input-2" /></Row>
          <Row label="Abholadresse"><input value={form.pickup_address} onChange={(e) => update("pickup_address", e.target.value)} className="input-2" /></Row>
          <Row label="Lieferadresse"><input value={form.delivery_address} onChange={(e) => update("delivery_address", e.target.value)} className="input-2" /></Row>
          <div className="grid grid-cols-3 gap-2">
            <Row label="Datum"><input type="date" value={form.moving_date} onChange={(e) => update("moving_date", e.target.value)} className="input-2" /></Row>
            <Row label="Uhrzeit"><input type="time" value={form.moving_time} onChange={(e) => update("moving_time", e.target.value)} className="input-2" data-testid="edit-time" /></Row>
            <Row label="Zeitfenster"><input value={form.time_window} onChange={(e) => update("time_window", e.target.value)} className="input-2" /></Row>
          </div>
          <Row label="Interne Notizen">
            <textarea rows={3} value={form.internal_notes} onChange={(e) => update("internal_notes", e.target.value)} className="input-2" data-testid="edit-notes" />
          </Row>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Abbrechen</button>
          <button
            onClick={() => {
              const patch = { ...form };
              patch.final_price = patch.final_price === "" ? null : +patch.final_price;
              onSave(patch);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-navy text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800"
            data-testid="edit-save"
          >
            <Save size={14} /> Speichern
          </button>
        </div>
        <style>{`.input-2 { width:100%; border:1px solid #e2e8f0; border-radius: 10px; padding: 8px 12px; outline:none; }
                 .input-2:focus { border-color: #F59E0B; box-shadow: 0 0 0 3px rgba(245,158,11,0.12); }`}</style>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function NewQuoteDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    type: "small",
    pickup_address: "",
    delivery_address: "",
    pickup_floor: 0,
    pickup_elevator: true,
    delivery_floor: 0,
    delivery_elevator: true,
    moving_date: new Date().toISOString().slice(0, 10),
    moving_time: "",
    time_window: "",
    first_name: "",
    last_name: "",
    phone: "",
    whatsapp: "",
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.first_name || !form.pickup_address || !form.delivery_address || !form.moving_date || !form.phone) {
      toast.error("Bitte Pflichtfelder ausfüllen");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/quotes", { ...form, photo_ids: [] });
      onCreated(data);
      toast.success("Termin angelegt");
    } catch {
      toast.error("Konnte Termin nicht anlegen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-floating w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 sm:p-7" onClick={(e) => e.stopPropagation()} data-testid="new-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-heading font-bold text-slate-900">Neuer Termin</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="space-y-3 text-sm">
          <Row label="Typ">
            <select value={form.type} onChange={(e) => update("type", e.target.value)} className="input-2">
              <option value="small">Kleine Transporte</option>
              <option value="full">Kompletter Umzug</option>
            </select>
          </Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Vorname *"><input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className="input-2" /></Row>
            <Row label="Nachname"><input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className="input-2" /></Row>
          </div>
          <Row label="Telefon *"><input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="input-2" /></Row>
          <Row label="Abholadresse *"><input value={form.pickup_address} onChange={(e) => update("pickup_address", e.target.value)} className="input-2" /></Row>
          <Row label="Lieferadresse *"><input value={form.delivery_address} onChange={(e) => update("delivery_address", e.target.value)} className="input-2" /></Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Etage (Abholung)">
              <input type="number" min={0} value={form.pickup_floor} onChange={(e) => update("pickup_floor", +e.target.value || 0)} className="input-2" />
            </Row>
            <Row label="Aufzug">
              <select value={form.pickup_elevator ? "1" : "0"} onChange={(e) => update("pickup_elevator", e.target.value === "1")} className="input-2">
                <option value="1">Ja</option>
                <option value="0">Nein</option>
              </select>
            </Row>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Etage (Lieferung)">
              <input type="number" min={0} value={form.delivery_floor} onChange={(e) => update("delivery_floor", +e.target.value || 0)} className="input-2" />
            </Row>
            <Row label="Aufzug">
              <select value={form.delivery_elevator ? "1" : "0"} onChange={(e) => update("delivery_elevator", e.target.value === "1")} className="input-2">
                <option value="1">Ja</option>
                <option value="0">Nein</option>
              </select>
            </Row>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Row label="Datum *"><input type="date" value={form.moving_date} onChange={(e) => update("moving_date", e.target.value)} className="input-2" /></Row>
            <Row label="Uhrzeit"><input type="time" value={form.moving_time} onChange={(e) => update("moving_time", e.target.value)} className="input-2" /></Row>
            <Row label="Zeitfenster"><input value={form.time_window} onChange={(e) => update("time_window", e.target.value)} className="input-2" /></Row>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Abbrechen</button>
          <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-brand-navy text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60" data-testid="new-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Anlegen
          </button>
        </div>
        <style>{`.input-2 { width:100%; border:1px solid #e2e8f0; border-radius: 10px; padding: 8px 12px; outline:none; }
                 .input-2:focus { border-color: #F59E0B; box-shadow: 0 0 0 3px rgba(245,158,11,0.12); }`}</style>
      </div>
    </div>
  );
}
